import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  TextField,
  Checkbox,
  Grid,
  IconButton,
} from "@mui/material";
import { SaveEliIcImportData } from "../../services/InstrumentDataService";
import SendIcon from "@mui/icons-material/Send";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import UserSelectorDialog from "../common/UserSelectorDialog";

const EliIC = () => {
  // State management
  const [fileData, setFileData] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isPosting, setIsPosting] = useState(false);

  // Updated analyst state to store user object instead of just string
  const [selectedAnalyst, setSelectedAnalyst] = useState(null);
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);

  // Form fields
  const [selectedProcess, setSelectedProcess] = useState("0003");
  const [batchNumber, setBatchNumber] = useState("");

  // Selection states
  const [selectedSamples, setSelectedSamples] = useState({});
  const [selectedAnalytes, setSelectedAnalytes] = useState({});
  const [selectAllSamples, setSelectAllSamples] = useState(true);
  const [selectAllAnalytes, setSelectAllAnalytes] = useState({});

  // Define analyte columns (TCAA removed as per feedback)
  const analyteColumns = [
    "Fluoride",
    "Chloride",
    "Bromide",
    "Nitrite",
    "Nitrate",
    "Sulfate",
    "Phosphate",
  ];

  // Initialize selection states
  useEffect(() => {
    if (processedData.length > 0) {
      // Initialize sample selections
      const sampleSelections = {};
      processedData.forEach((_, index) => {
        sampleSelections[index] = true;
      });
      setSelectedSamples(sampleSelections);

      // Initialize analyte selections
      const analyteSelections = {};
      const selectAllAnalyteStates = {};
      analyteColumns.forEach((analyte) => {
        analyteSelections[analyte] = {};
        selectAllAnalyteStates[analyte] = false;
        processedData.forEach((_, index) => {
          analyteSelections[analyte][index] = false;
        });
      });
      setSelectedAnalytes(analyteSelections);
      setSelectAllAnalytes(selectAllAnalyteStates);
    }
  }, [processedData]);

  const handleAnalystSelect = (user) => {
    setSelectedAnalyst(user);
  };


  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const rows = csvContent.split("\n").map((row) => row.split(","));

        // Store raw data
        setFileData(rows);

        // Process the data
        const processed = processCSVData(rows, csvContent);
        setProcessedData(processed);
        NotificationManager.success(
          "Success",
          `File processed successfully. Found ${processed.length} samples.`
        );
        setIsProcessing(false);
      } catch (error) {
        console.error("Error processing file:", error);
        NotificationManager.error(
          "Errors",
          `Error processing file: ${error.message}`
        );
        setIsProcessing(false);
      }
    };

    reader.readAsText(file);
  };

  const ExcelDateToJSDate = (excelDate, isForOrderCreation = false) => {
    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
    
    if (isForOrderCreation) {
      jsDate.setDate(jsDate.getDate() + 1);
    }
    
    return jsDate;
  };

  const processCSVData = (rows, csvContent) => {
    let headerRowIndex = -1;
    let dataStartIndex = -1;

    // Look for header row containing analyte columns (excluding TCAA)
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].some((cell) => analyteColumns.includes(cell?.trim()))) {
        headerRowIndex = i;
        dataStartIndex = i + 2;
        break;
      }
    }

    if (headerRowIndex === -1) {
      throw new Error("Could not find analyte header row");
    }

    const headers = rows[headerRowIndex].map((h) => h?.trim()).filter((h) => h);
    const processed = [];

    for (let i = dataStartIndex; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length < 2 || !row[1]?.trim()) continue;

      const injectionName = row[1]?.trim().replace(/"/g, "");

      if (
        injectionName.includes("CCV") ||
        injectionName.includes("LCCV") ||
        injectionName.includes("TCAA") ||
        injectionName.includes("ECCV") ||
        injectionName.includes("SHUTDOWN") ||
        injectionName.includes("MS ") ||
        injectionName.includes("MSD ")
      ) {
        continue;
      }

      const sampleInfo = parseSampleName(injectionName);
      if (!sampleInfo) continue;

      const testDateRaw = row[row.length - 2]?.trim();
      let testDateTime = null;
      if (testDateRaw) {
        const asNumber = Number(testDateRaw);
        if (!isNaN(asNumber)) {
          testDateTime = ExcelDateToJSDate(asNumber);
        } else {
          const parsedDate = new Date(testDateRaw);
          testDateTime = isNaN(parsedDate.getTime()) ? null : parsedDate;
        }
      }

      const sampleData = {
        originalName: injectionName,
        testDate: testDateTime,
        ...sampleInfo,
        analytes: {},
        isQC: isQCSample(sampleInfo.sampleId),
      };

      headers.forEach((header, index) => {
        if (header === "TCAA") {
          return;
        }

        if (analyteColumns.includes(header) && row[index + 2]) {
          let value = row[index + 2]?.trim();

          if (
            value === "n.a." ||
            value === "N.A." ||
            value === "" ||
            value === undefined ||
            value.startsWith("-")
          ) {
            value = "0";
          } else {
            try {
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                value = numValue.toString();
              } else {
                value = "0";
              }
            } catch {
              value = "0";
            }
          }
          sampleData.analytes[header] = value;
        }
      });

      processed.push(sampleData);
    }

    return processed;
  };

  const parseSampleName = (injectionName) => {
    let orderNumber = "";
    let sampleId = "";
    let sampleNumber = "";
    let dilutionFactor = "1";
    let notes = "";

    if (injectionName.includes(";")) {
      const parts = injectionName.split(";");
      const namePart = parts[0].trim();
      const dfPart = parts[1] ? parts[1].trim() : "";

      const dfMatch = dfPart.match(/(?:DF)?(\d+)/);
      if (dfMatch) {
        dilutionFactor = dfMatch[1];
      }

      const notesMatch = dfPart.match(/DF\d+([_-].*)/);
      if (notesMatch) {
        notes = notesMatch[1];
      }

      if (namePart.includes("_")) {
        const underscoreParts = namePart.split("_");
        if (underscoreParts.length >= 2) {
          orderNumber = underscoreParts[0];
          sampleId = underscoreParts[1];
        }
      } else {
        sampleId = namePart;
      }
    } else {
      if (injectionName.includes(" DF")) {
        const parts = injectionName.split(" DF");
        const namePart = parts[0];
        const dfPart = parts[1];

        const dfMatch = dfPart.match(/^(\d+)/);
        if (dfMatch) {
          dilutionFactor = dfMatch[1];
        }

        const notesMatch = dfPart.match(/[_-](.+)$/);
        if (notesMatch) {
          notes = notesMatch[1];
        }

        if (namePart.includes("_")) {
          const parts = namePart.split("_");
          orderNumber = parts[0];
          sampleId = parts[1] || namePart;
        } else if (namePart.includes("-")) {
          const dashParts = namePart.split("-");
          if (dashParts.length >= 2) {
            if (dashParts[0].match(/^\d{10}$/)) {
              orderNumber = dashParts[0];
              sampleId = namePart;
            } else {
              sampleId = namePart;
              orderNumber = dashParts[0];
            }
          }
        } else {
          sampleId = namePart;
        }
      } else {
        if (injectionName.includes("_")) {
          const parts = injectionName.split("_");
          orderNumber = parts[0];
          sampleId = parts[1] || injectionName;
        } else {
          sampleId = injectionName;
        }
      }
    }

    const sampleNumberMatch = sampleId.match(/-((\d+)|U\d+)$/);
    if (sampleNumberMatch) {
      sampleNumber = sampleNumberMatch[1];
    }

    if (!sampleId) return null;

    return {
      orderNumber,
      sampleId,
      sampleNumber,
      dilutionFactor,
      notes,
      formattedName: orderNumber ? `${orderNumber}_${sampleId}` : sampleId,
    };
  };

  const isQCSample = (sampleId) => {
    const qcTypes = ["DUP", "MS", "MSD", "BLANK", "CCB", "CCV", "LCCV"];
    return qcTypes.some((qc) => sampleId.includes(qc));
  };

  const handleSampleSelection = (index, checked) => {
    setSelectedSamples((prev) => ({
      ...prev,
      [index]: checked,
    }));
  };

  const handleAnalyteSelection = (analyte, index, checked) => {
    setSelectedAnalytes((prev) => ({
      ...prev,
      [analyte]: {
        ...prev[analyte],
        [index]: checked,
      },
    }));
  };

  const handleSelectAllSamples = (checked) => {
    setSelectAllSamples(checked);
    const newSelections = {};
    processedData.forEach((_, index) => {
      newSelections[index] = checked;
    });
    setSelectedSamples(newSelections);
  };

  const handleSelectAllAnalytes = (analyte, checked) => {
    setSelectAllAnalytes((prev) => ({
      ...prev,
      [analyte]: checked,
    }));

    setSelectedAnalytes((prev) => ({
      ...prev,
      [analyte]: Object.keys(prev[analyte]).reduce((acc, key) => {
        acc[key] = checked;
        return acc;
      }, {}),
    }));
  };

  const validateForm = () => {
    if (!fileName) {
      NotificationManager.error("Errors", "Please upload a file first");
      return true;
    }

    if (!batchNumber || parseInt(batchNumber) === 0) {
      NotificationManager.error("Errors", "Please enter a valid batch number");
      return true;
    }

    // Updated validation to check for selected analyst object
    if (!selectedAnalyst || !selectedAnalyst.operator) {
      NotificationManager.error("Errors", "Please select an analyst");
      return true;
    }

    const hasSelectedSamples = Object.values(selectedSamples).some(
      (selected) => selected
    );
    if (!hasSelectedSamples) {
      NotificationManager.error("Errors", "Please select at least one sample");
      return true;
    }

    const hasSelectedAnalytes = Object.values(selectedAnalytes).some(
      (analyteObj) => Object.values(analyteObj).some((selected) => selected)
    );
    if (!hasSelectedAnalytes) {
      NotificationManager.error(
        "Errors",
        "Please select at least one analyte/chemical value"
      );
      return true;
    }

    return false;
  };

  const resetAllData = () => {
    setFileData([]);
    setProcessedData([]);
    setFileName("");
    setBatchNumber("");
    setSelectedAnalyst(null);
    setSelectedSamples({});
    setSelectedAnalytes({});
    setSelectAllSamples(true);
    setSelectAllAnalytes({});
    setIsProcessing(false);
    setIsPosting(false);

    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (validationErrors) {
      return;
    }

    setIsPosting(true);

    try {
      const samples = [];

      processedData.forEach((sample, index) => {
        if (!selectedSamples[index]) return;

        const selectedAnalyteNames = analyteColumns.filter(
          (analyte) => selectedAnalytes[analyte]?.[index]
        );

        if (selectedAnalyteNames.length === 0) return;

        const analyteValues = {};

        analyteColumns.forEach((analyte) => {
          analyteValues[analyte] = selectedAnalyteNames.includes(analyte)
            ? sample.analytes[analyte]
            : "";
        });

        samples.push({
          orderNumber: sample.orderNumber,
          sampleId: sample.sampleId,
          testDate: formatDateForAPI(sample.testDate),
          dilutionFactor: sample.dilutionFactor,
          ...analyteValues,
        });
      });

      const submissionData = {
        fileName,
        processId: selectedProcess,
        batchNumber: parseInt(batchNumber),
        analyst: selectedAnalyst.operator, // Use operator from selected user
        testDate: new Date().toISOString(),
        samples,
      };

      console.log("Final Submission Object:", submissionData);

      NotificationManager.info(
        "Info",
        `Successfully prepared ${samples.length} samples for submission.`
      );
      let response = await SaveEliIcImportData(submissionData);
      NotificationManager.success(
        "Success",
        `Results imported successfully. Selected samples are updated.`
      );
      console.log("API response :  ", response);
      resetAllData();
    } catch (error) {
      console.error("Error preparing submission:", error);
      NotificationManager.error(
        "Errors",
        `Error posting data: ${error.message}`
      );
      setIsPosting(false);
    }
  };

  const getSampleTypeChip = (sample) => {
    if (sample.isQC) {
      return <Chip label="QC" color="warning" size="small" />;
    }
    return <Chip label="Sample" color="primary" size="small" />;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "-";
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  const formatDateForAPI = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const getAnalyteValue = (sample, analyte) => {
    const value = sample.analytes[analyte];
    if (!value) {
      return <span style={{ color: "#999" }}>-</span>;
    }
    return value;
  };

  return (
    <div className="vlp-component-container">
      <span className="title">ELI IC Import</span>
      <hr />

      <Box sx={{ p: 3, maxWidth: "90%", margin: "10", padding: "0" }}>
        {/* File Upload and Configuration */}
        <>
          <Grid
            container
            spacing={2}
            alignItems="center"
            wrap="wrap"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Updated analyst selection section */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedAnalyst ? (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 1, 
                      border: 1, 
                      borderColor: 'grey.300', 
                      borderRadius: 1,
                      backgroundColor: 'grey.50',
                      minHeight: '40px'
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedAnalyst.operator} - {selectedAnalyst.aliasName}
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => setUserSelectorOpen(true)}
                      sx={{ color: 'primary.main', ml: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<PersonIcon />}
                    onClick={() => setUserSelectorOpen(true)}
                    sx={{ 
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      height: '40px'
                    }}
                  >
                    Select Analyst
                  </Button>
                )}
              </Box>
            </Grid>

            {selectedAnalyst && (
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <TextField
                  label="QC Batch"
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                  fullWidth
                  type="number"
                  placeholder="Enter batch number"
                  size="small"
                />
              </Grid>
            )}

            {batchNumber !== "" && (
              <Grid item xs={12} sm={4} md={3} lg={2}>
                <input
                  accept=".csv"
                  style={{ display: "none" }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                  disabled={isProcessing}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    size="small"
                    startIcon={<FileUploadIcon />}
                    disabled={isProcessing}
                  >
                    Upload CSV
                  </Button>
                </label>
              </Grid>
            )}

            <Grid item>
              {fileName && (
                <Chip
                  icon={<AttachFileIcon fontSize="small" />}
                  label={fileName}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Grid>

            {processedData.length !== 0 && (
              <Grid item>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={isPosting || processedData.length === 0}
                >
                  {isPosting ? "Posting..." : "Post"}
                </Button>
              </Grid>
            )}
          </Grid>
          <br />
        </>
        <CommonLoader loading={isProcessing || isPosting} />

        {/* Data Table */}
        {processedData.length > 0 && (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table sx={{ minWidth: 700 }} size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectAllSamples}
                        onChange={(e) =>
                          handleSelectAllSamples(e.target.checked)
                        }
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <strong>Order Number</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Sample Number</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Sample ID</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Test Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Analyst</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>DF</strong>
                    </TableCell>
                    {analyteColumns.map((analyte) => (
                      <TableCell key={analyte} align="center">
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Checkbox
                            checked={selectAllAnalytes[analyte] || false}
                            onChange={(e) =>
                              handleSelectAllAnalytes(analyte, e.target.checked)
                            }
                            color="success"
                            size="small"
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            {analyte}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedData.map((sample, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSamples[index] || false}
                          onChange={(e) =>
                            handleSampleSelection(index, e.target.checked)
                          }
                          color="success"
                        />
                      </TableCell>
                      <TableCell>{sample.orderNumber || "-"}</TableCell>
                      <TableCell>{sample.sampleNumber || "-"}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {sample.sampleId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {sample.testDate ? formatDateForDisplay(sample.testDate) : "-"}
                      </TableCell>
                      <TableCell>
                        {selectedAnalyst ? selectedAnalyst.operator : "-"}
                      </TableCell>
                      <TableCell>{getSampleTypeChip(sample)}</TableCell>
                      <TableCell>
                        <Chip
                          label={sample.dilutionFactor}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      {analyteColumns.map((analyte) => (
                        <TableCell key={analyte} align="center">
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <Checkbox
                              checked={
                                selectedAnalytes[analyte]?.[index] || false
                              }
                              onChange={(e) =>
                                handleAnalyteSelection(
                                  analyte,
                                  index,
                                  e.target.checked
                                )
                              }
                              color="success"
                              size="small"
                              disabled={!selectedSamples[index]}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              {getAnalyteValue(sample, analyte)}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Selected:</strong>{" "}
                {Object.values(selectedSamples).filter(Boolean).length} samples
                |<strong> Total Analyte Measurements:</strong>{" "}
                {Object.values(selectedAnalytes).reduce(
                  (total, analyteObj) =>
                    total + Object.values(analyteObj).filter(Boolean).length,
                  0
                )}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* User Selector Dialog */}
      <UserSelectorDialog
        open={userSelectorOpen}
        onClose={() => setUserSelectorOpen(false)}
        onSelect={handleAnalystSelect}
        multiSelect={false}
        filter="ANALYSTS"
      />
    </div>
  );
};

export default EliIC;